/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { IModelConnection } from "@itwin/core-frontend";
import { ViewState, ViewCreator3d } from "@itwin/core-frontend";
import SavedViewsApi  from "./SavedViewsApi";
import { SavedViewItwin3dViewInterface } from "./SavedViewsInterfaces";

export class ViewSetup {

  public static savedViewObject : SavedViewItwin3dViewInterface | undefined; 
  public static viewStateObject : ViewState;
  /** Queries for and loads the default view for an iModel. */
  public static getDefaultView = async (imodel: IModelConnection): Promise<ViewState> => {
    const iTwinId = imodel.iTwinId ?? "";
    const iModelId = imodel.iModelId ?? "";

    // Store the saved view id in local storage
    let sViewId = localStorage.getItem("saved-view-id-"+iModelId);
    let savedViewResponse;
    if(sViewId) {
      savedViewResponse = await SavedViewsApi.getByIdAsync(sViewId ?? "");
      // If the saved view ID from localStorage doesn't work, try the fallback
      if (!savedViewResponse) {
        console.warn(`Saved view with ID ${sViewId} not found, trying fallback approach`);
        sViewId = null; // Clear the invalid ID to trigger fallback
      }
    } 
    
    if (!sViewId) {
      const allSavedViews = await SavedViewsApi.getAllAsync(iTwinId, iModelId);
      let savedViewId = allSavedViews.savedViews.find((saveview) => saveview.displayName === process.env.IMJS_SAVED_VIEW_DISPLAY_NAME)?.id;  
      
      // Fallback: if no saved view matches the environment variable, use the first available one
      if (!savedViewId && allSavedViews.savedViews.length > 0) {
        savedViewId = allSavedViews.savedViews[0].id;
        console.warn(`No saved view found with display name: ${process.env.IMJS_SAVED_VIEW_DISPLAY_NAME}. Using first available saved view: ${allSavedViews.savedViews[0].displayName}`);
      }
      
      if (!savedViewId) {
        throw new Error(`No saved views available for iTwin: ${iTwinId}, iModel: ${iModelId}`);
      }
      
      savedViewResponse = await SavedViewsApi.getByIdAsync(savedViewId ?? "");
      if(savedViewId !== undefined) {
        localStorage.setItem("saved-view-id-"+iModelId, savedViewId);
      }      
    }
   
    // Check if savedViewResponse exists before accessing its properties
    if (!savedViewResponse || !savedViewResponse.savedView) {
      throw new Error("No saved view found or savedViewResponse is undefined");
    }

    console.log("Saved view response structure:", {
      hasResponse: !!savedViewResponse,
      hasSavedView: !!savedViewResponse.savedView,
      hasSavedViewData: !!savedViewResponse.savedView.savedViewData,
      savedViewDataKeys: savedViewResponse.savedView.savedViewData ? Object.keys(savedViewResponse.savedView.savedViewData) : [],
      displayName: savedViewResponse.savedView.displayName
    });

    // Get the saved view based on SAVED_VIEW_DISPLAY_NAME
    // Try to get the view data from different possible properties
    const savedViewData = savedViewResponse.savedView.savedViewData;
    const savedView = savedViewData?.legacyView || savedViewData?.itwin3dView;

    // Check if savedView exists before creating view state
    if (!savedView) {
      console.error("Saved view data structure:", savedViewData);
      console.warn("No valid view data found in saved view. Creating default view instead.");
      
      // Create a default view when no saved view data is available
      const defaultViewState = await new ViewCreator3d(imodel).createDefaultView();
      if (defaultViewState) {
        ViewSetup.savedViewObject = undefined;
        ViewSetup.viewStateObject = defaultViewState;
        return defaultViewState;
      }
      
      throw new Error("No valid view data found and unable to create default view. Neither legacyView nor itwin3dView is available in savedViewData.");
    }

    // Create the view state using savedView
    const viewState = await ViewSetup.createSpatialViewStateFromSavedView(imodel, savedView);

    ViewSetup.savedViewObject = savedView;
    ViewSetup.viewStateObject = viewState;

    return viewState;
  };

  /**
   * Create a spatial view state from saved view data, with fallback to default view
   */
  private static createSpatialViewStateFromSavedView = async (
    iModelConnection: IModelConnection,
    savedView: SavedViewItwin3dViewInterface
  ): Promise<ViewState> => {
    try {
      console.log("Creating view state with saved view data:", {
        hasOrigin: !!savedView.origin,
        hasExtents: !!savedView.extents,
        hasAngles: !!savedView.angles,
        hasCamera: !!savedView.camera,
        hasCategories: !!savedView.categories,
        hasModels: !!savedView.models,
        categoriesEnabled: savedView.categories?.enabled,
        modelsEnabled: savedView.models?.enabled
      });

      // Use ViewCreator3d to create a default view, then apply saved view settings
      const viewState = await new ViewCreator3d(iModelConnection).createDefaultView({ 
        cameraOn: savedView.camera ? true : false 
      }, savedView.models?.enabled);
      
      console.log("Created base view state:", {
        isValid: !!viewState,
        is3d: viewState?.is3d(),
        hasDisplayStyle: !!viewState?.displayStyle,
        categoryCount: viewState?.categorySelector?.categories?.size || 0,
        modelCount: viewState.is3d() ? (viewState as any).modelSelector?.models?.size || 0 : 0
      });
      
      // Apply saved view settings if they exist
      if (savedView.origin) {
        const Point3d = await import("@itwin/core-geometry").then(m => m.Point3d);
        viewState.setOrigin(Point3d.fromJSON(savedView.origin));
        console.log("Applied origin:", savedView.origin);
      }
      
      if (savedView.extents) {
        const Vector3d = await import("@itwin/core-geometry").then(m => m.Vector3d);
        viewState.setExtents(Vector3d.fromJSON(savedView.extents));
        console.log("Applied extents:", savedView.extents);
      }
      
      if (savedView.angles) {
        const YawPitchRollAngles = await import("@itwin/core-geometry").then(m => m.YawPitchRollAngles);
        const angles = YawPitchRollAngles.fromJSON(savedView.angles);
        viewState.setRotation(angles.toMatrix3d());
        console.log("Applied angles:", savedView.angles);
      }
      
      if (viewState.is3d() && savedView.camera) {
        const Camera = await import("@itwin/core-common").then(m => m.Camera);
        const camera = new Camera(savedView.camera);
        viewState.camera.setFrom(camera);
        console.log("Applied camera settings");
      }
      
      if (savedView.categories?.enabled && savedView.categories.enabled.length > 0) {
        viewState.categorySelector.categories.clear();
        viewState.categorySelector.addCategories(savedView.categories.enabled);
        console.log("Applied categories:", savedView.categories.enabled.length, "categories");
      } else {
        // If no categories from saved view, ensure some categories are enabled
        console.log("No categories in saved view, checking if any are enabled by default");
        if (viewState.categorySelector.categories.size === 0) {
          console.warn("No categories enabled - this will result in empty/dark view");
          // Try to enable all available categories as a fallback
          try {
            const allCategories = await iModelConnection.query("SELECT ECInstanceId FROM BisCore.Category");
            const categoryIds: string[] = [];
            for await (const row of allCategories) {
              categoryIds.push(row.id);
            }
            if (categoryIds.length > 0) {
              viewState.categorySelector.addCategories(categoryIds);
              console.log("Enabled all available categories as fallback:", categoryIds.length);
            }
          } catch (error) {
            console.warn("Could not query for categories:", error);
          }
        }
      }
      
      // Ensure the display style has proper lighting and background settings
      if (viewState.displayStyle) {
        const displayStyle = viewState.displayStyle;
        console.log("Display style type:", displayStyle.is3d() ? "3D" : "2D");
        
        // Get current background color and log it
        const backgroundColor = displayStyle.backgroundColor;
        console.log("Current background color (tbgr):", backgroundColor.tbgr);
        
        // If background is too dark (very close to black), set a lighter one
        const tbgr = backgroundColor.tbgr;
        if (tbgr < 0x333333 || tbgr === 0) { // If TBGR value is very dark or black
          console.log("Background too dark, setting lighter background");
          const ColorDef = await import("@itwin/core-common").then(m => m.ColorDef);
          displayStyle.backgroundColor = ColorDef.white; // Set to white for now
        }
        
        // Ensure models and categories are visible
        if (viewState.is3d()) {
          const view3d = viewState as any;
          console.log("3D View - Models enabled:", view3d.modelSelector?.models?.size || 0);
          console.log("3D View - Categories enabled:", viewState.categorySelector?.categories?.size || 0);
          
          // If no models are enabled, try to enable default models
          if (view3d.modelSelector && view3d.modelSelector.models.size === 0) {
            console.warn("No models enabled, this might cause dark screen");
            // Try to enable all available spatial models
            try {
              const spatialModels = await iModelConnection.query("SELECT ECInstanceId FROM BisCore.SpatialModel");
              const modelIds: string[] = [];
              for await (const row of spatialModels) {
                modelIds.push(row.id);
              }
              if (modelIds.length > 0) {
                view3d.modelSelector.addModels(modelIds);
                console.log("Enabled spatial models as fallback:", modelIds.length);
              }
            } catch (error) {
              console.warn("Could not query for spatial models:", error);
            }
          }
        }
        
        // If no categories are enabled, this will cause everything to be invisible
        if (viewState.categorySelector.categories.size === 0) {
          console.log("No categories enabled, this will cause dark/empty screen");
        }
      }
      
      console.log("Final view state setup complete");
      return viewState;
    } catch (error) {
      console.error("Error creating view state from saved view:", error);
      console.warn("Falling back to simple default view");
      
      // Final fallback - just create a simple default view
      return await new ViewCreator3d(iModelConnection).createDefaultView();
    }
  };

}
